import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card } from '@/components/ui/card'
import { usePermission } from '@/hooks/usePermission'
import { usePrivacySettings } from '@/hooks/usePrivacySettings'
import { apiClient, toApiProblem } from '@/lib/api/client'

/**
 * "Employees can see the company directory" — the company's choice, closed by default.
 *
 * <p>Closed, an employee sees themselves and the people who report to them, and the directory
 * and org chart leave their menu. Open, everybody can look colleagues up by name, department
 * and job, as an office staff list does. Contact details stay hidden either way.
 */
export function DirectoryPrivacyCard() {
  const { employeeDirectoryVisible } = usePrivacySettings()
  const canWrite = usePermission('settings:write')
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async (visible: boolean) =>
      (await apiClient.put<{ employeeDirectoryVisible: boolean }>(
        '/v1/settings/privacy', { employeeDirectoryVisible: visible })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings', 'privacy'] }),
  })

  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">Privacy</h3>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4"
          checked={employeeDirectoryVisible}
          disabled={!canWrite || save.isPending}
          onChange={(event) => {
            setError(null)
            save.mutate(event.target.checked, {
              onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'The setting could not be saved.'),
            })
          }}
        />
        <span>
          Employees can see the company directory
          <span className="block text-xs text-[var(--color-muted)]">
            Off: employees see only themselves and the people who report to them. On: everybody
            can look up colleagues by name, department and job. Phone numbers and emails stay
            hidden either way. HR always sees everyone.
          </span>
        </span>
      </label>
      {error && <p role="alert" className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>}
    </Card>
  )
}
